import type { NitroFetchOptions } from "nitropack";
import { UnauthenticatedError, getDomainError } from "~/composables/api/errors";
import { useIsCrowdfunding } from "~/composables/useIsCrowdfunding";
import { useSendClientErrorToSentry } from "~/composables/useSendClientErrorToSentry";
import { useWebviewExtraHeaders } from "~/composables/useWebviewExtraHeaders";

const makeCorsHeader = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  "Access-Control-Allow-Headers": "*",
  "Access-Control-Allow-Origin": "*",
  "Access-Control-Allow-Methods": "DELETE, GET, POST, PATCH, PUT, OPTIONS",
  "X-Content-Type-Options": "nosniff",
});

const makeSecurityHeader = () => ({
  "Content-Security-Policy": "default-src https frame-ancestors 'none'",
  "Referrer-Policy": "same-origin",
  "X-Frame-Options": "DENY",
  "X-XSS-Protection": "1; mode=block",
  "X-Is-Webapp": "true",
});

export default defineNuxtPlugin(() => {
  const { $performTokenRefresh, $refreshLock, $token } = useNuxtApp();
  const isCrowdfunding = useIsCrowdfunding();
  const env = useRuntimeConfig();
  const signOut = useSignOut();
  const webviewExtraHeaders = useWebviewExtraHeaders();
  const accountId = useCookie("account", {
    path: "/",
    maxAge: 60 * 60 * 24 * 30,
    domain: env.public.hurstDomain as string | undefined,
  });

  const sendErrorToSentry = useSendClientErrorToSentry();

  const getDefaultHeaders = () => {
    return {
      ...makeCorsHeader(),
      ...makeSecurityHeader(),
      ...(isCrowdfunding ? { "x-website-type": "crowdfunding" } : {}),
      ...webviewExtraHeaders.value,
    };
  };

  const createFetchClient = ({
    baseUrl,
    headers,
  }: {
    baseUrl: string;
    headers?: any;
  }) => {
    const wrappedFetch = async <T>(
      path: string,
      options: NitroFetchOptions<any> = {},
    ) => {
      try {
        return await $fetch<T>(path, {
          ...options,
          headers: {
            ...options.headers,
            ...($token.value && { Authorization: $token.value }),
            ...(accountId.value && { "x-account-id": accountId.value }),
          },
        });
      } catch (error) {
        throw getDomainError(error);
      }
    };

    const makeRequest = async <T>(
      path: string,
      options: NitroFetchOptions<any> = {},
    ) => {
      try {
        if (typeof window !== "undefined") {
          await $performTokenRefresh();
        }
        await $refreshLock();
        return await wrappedFetch<T>(path, {
          ...options,
          baseURL: baseUrl,
          headers,
        });
      } catch (error) {
        if (error instanceof UnauthenticatedError && $token.value) {
          await $performTokenRefresh();

          try {
            return await wrappedFetch<T>(path, {
              ...options,
              baseURL: baseUrl,
              headers,
            });
          } catch (error) {
            sendErrorToSentry(error);
            if (error instanceof UnauthenticatedError) {
              signOut();
            }
            throw error;
          }
        }
        throw error;
      }
    };

    return makeRequest;
  };

  const api = createFetchClient({
    baseUrl: env.public.apiBaseURL,
    headers: getDefaultHeaders(),
  });
  const operationV2Api = createFetchClient({
    baseUrl: env.public.apiOperationV2BaseURL,
    headers: getDefaultHeaders(),
  });
  const authApi = createFetchClient({
    baseUrl: env.public.authBaseURL,
    headers: { "Content-Type": "application/json" },
  });

  return {
    provide: {
      api,
      operationV2Api,
      authApi,
    },
  };
});
